import React, { useEffect, useState } from 'react'
import { withFormik, Form, Field } from 'formik'
import { Link, Redirect, useParams } from 'react-router-dom'
import InfoBox from './InfoBox'
import { authenticate, userStatus, resetPassword, accountLocked } from '../utils/authService';
import { withTranslation } from 'react-i18next';

const validate = (values, props) => {
  let errors = {};

  if (!values.userName) {
    errors.userName = props.t('enterUserName');
  }

  if (!values.password) {
    errors.password = props.t('passwordRequired');
  }

  return errors;
};

const LoginComponent = (props) => {
  const {
    values,
    handleChange,
    dirty,
    errors,
    touched,
    t,
    isSubmitting,
    status={ locked: false, error: null },
    setStatus
  } = props

  const [capsLock, setCapsLock] = useState(false);
  
  useEffect(() => {
    document.title = `City of Seattle | ${t('login')}`
  }, [t])

  useEffect(() => {
    document.getElementById("userName").focus()
  }, [])

  useEffect(() => {
    setStatus({locked: false, error: null})
  }, [setStatus])

  const handleKeyDown = (e) => {
    const checkLock = e.getModifierState('CapsLock');
    if(checkLock) {
      setCapsLock(checkLock);
    } else {
      setCapsLock(checkLock);
    }
  }
    

    function getParameterByName(name, url = window.location.href) {
        name = name.replace(/[\[\]]/g, '\\$&');
        var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    var appName = getParameterByName('appName');

    var link = '/registration?appName=' + appName;
    var resetLink = '/forgot-password?appName=' + appName;
    var forgotUserNameLink = '/forgot-username?appName=' + appName;
    values.appName = appName;

  if(status.error) {
    return (
      <Redirect to="/error" />
    )
  }
  return (
    <div>
      <h2>{t('login')}</h2>
      <Form className="App-form" onKeyDown={handleKeyDown}>
        <div className={"form-group form-group-lg " + (errors.userName && touched.userName ? 'has-error' : '')}>
          <label htmlFor="userName" className="control-label">{t('userName')} <span className="text-danger">*</span></label>
          <Field id="userName" type="userName" className="form-control" name="userName" value={values.userName} onChange={handleChange} />
          {errors.userName && touched.userName && <div className="text-danger exception-margin" role="alert">
            <span className="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>
            <span className="sr-only">Error:</span>
            {touched.userName && errors.userName}
          </div>}
        </div>

        <div className={"form-group form-group-lg " + (errors.password && touched.password ? 'has-error' : '')}>
          <label htmlFor="password" className="control-label">{t('password')} <span className="text-danger">*</span></label>
          <Field type="password" className="form-control" name="password" placeholder="Password" value={values.password} onChange={handleChange} />
          {capsLock && ( 
            <div className="exception-margin">
              <span className="text-warning" role="alert">
                <span className="glyphicon glyphicon-warning-sign" aria-hidden="true"></span>
                <span className="sr-only">{t('capsLockIsOn')}</span>
              </span>
              {t('capsLockIsOn')}
            </div>
          )}
          {errors.password && touched.password && <div className={capsLock ? "text-danger" : "text-danger exception-margin"} role="alert">
            <span className="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>
            <span className="sr-only">Error:</span>
            {touched.password && errors.password}
          </div>}
        </div>
        <div className="form-group form-group-lg">
          <label>
            <Field className="App-rememberMe" type="checkbox" name="rememberMe" checked={values.rememberMe}></Field>
            {' '}{t('rememberMe')}
          </label>
        </div>
        <button type="submit" className="btn btn-primary btn-lg btn-block">{t('login')}</button>
      </Form>
      <div style={{ 'display': 'flex', 'justifyContent': 'space-between', 'flexWrap': 'wrap','width': '100%', 'minHeight': '40px', 'padding': '5px 0' }}>
        <div style={{ 'display': 'flex', 'flexWrap': 'wrap' }}>
                  <Link to={resetLink} style={{ 'paddingRight': '5px' }}>{t('forgotPassword')}</Link>
                  <Link to={forgotUserNameLink}>{t('forgotUsername')}</Link>
        </div>
              <div>{t('doNotHaveAccount')} <Link to={link}>{t('register')}</Link></div>
      </div>
      <InfoBox></InfoBox>
    </div>

  )
}

const Login = withFormik({
  mapPropsToValues({ userName, password, rememberMe }) {
    const uName = window.localStorage.getItem('uName');
    if (uName) {
      userName = uName;
      rememberMe = true;
    }
    return {
      userName: userName || '',
      password: password || '',
      rememberMe: rememberMe || false,
    }
  },
  validate: validate,
  handleSubmit(values, { props, setSubmitting, setStatus }) {
    values.userName = values.userName.trim();
    authenticate(values).then(response => {
      if (values.rememberMe) {
        window.localStorage.setItem('uName', values.userName);
      } else {
        window.localStorage.removeItem('uName');
      }
      if (response && response.data) {
        props.setAccessToken(response.data.access_token);
      }
      window.localStorage.removeItem(values.userName);
      setSubmitting(false);

    }).catch(error => {     
      if(error.response.status > 401) {
        setStatus({error: true});
        return;
      }

      const maxLoginAttempts = 5
      const loginAttempts = window.localStorage.getItem(values.userName)
        ? parseInt(window.localStorage.getItem(values.userName)) + 1
        : 1

      if (loginAttempts < maxLoginAttempts) {
        setSubmitting(true)
        userStatus(encodeURIComponent(values.userName.trim()))
          .then(response => {
            //console.log(response.data);
            if (response.status >= 500) {
              setStatus({error: true});
              return;
            }

            if (response.data && response.data !== "Invalid User") {
              let inNew, inTest;
              const groups = response.data.groups // ATTN: 'groups' property doesn't seem to currently exist (setting conditional below in case this changes)
              //console.log(groups);
              
              if (groups.length > 0) {
                const firstLoginGroup = 'NewlyProvisioned'
                const firstLoginGroupTest = 'KubraImport'   // temp group for QA testing
                inNew = groups.includes(firstLoginGroup)
                inTest = groups.includes(firstLoginGroupTest)
              }

              if (inNew || inTest) {    // refactor after QA testing
                setStatus({locked: true})
                resetPassword(values.userName, values.appName)
                  .then(() => {
                    props.showNotification({
                      type: 'kubra',
                      title: props.t('kubraTitle'),
                      message: props.t('kubraMessage')
                    })
                  })
              } else {
                const isLocked = (response.data.locked === "true");
                const isVerified = (response.data.status === "active");

                if (isLocked) {
                  setStatus({locked: true})
                  if (values.passwordResetLockedAccountSent !== true) {
                    accountLocked(values.userName, values.appName).then(response => {
                      values.passwordResetLockedAccountSent = true
                    })
                  }
                  props.showNotification({
                      type: 'failure',
                      title: props.t('loginFailedTitle'),
                      message: props.t('acctLocked')
                  })
                } else {
                  if (isVerified) {
                    window.localStorage.setItem(values.userName, loginAttempts)
 
                    const message = error.response.data.error === 'invalid_grant' ?
                      props.t('loginFailed', { errCnt: maxLoginAttempts - loginAttempts }) :
                      error.response.data.error_description.replace('account', 'profile');
 
                    props.showNotification({
                      type: 'failure',
                      title: props.t('loginFailedTitle'),
                      message: message
                    });
                  } else {
                    props.showNotification({
                      type: 'failure',
                      title: props.t('forgotUsernameFailedTitle'),
                      message: props.t('loginFailedInactivated')
                    });
                  }
                }
              }
            } else {    // username not found

              window.localStorage.setItem(values.userName, loginAttempts)

              if (loginAttempts >= maxLoginAttempts) {
                if (values.passwordResetLockedAccountSent !== true) {
                    accountLocked(values.userName, values.appName).then(response => {
                        values.passwordResetLockedAccountSent = true
                    })
                }
                setStatus({locked: true})
                props.showNotification({
                  type: 'failure',
                  title: props.t('acctLockedTitle'),
                  message: props.t('acctLocked')
                })
              } else {
                props.showNotification({
                  type: 'failure',
                  title: props.t('loginFailedTitle'),
                  message: props.t('loginFailed', {
                    errCnt: maxLoginAttempts - loginAttempts
                  })
                })
              }
            }
            setSubmitting(false)
          }).catch(error => {
            if (error.response.status >= 500) {
              setStatus({ error: true });
              return;
            }
            console.log(error)
          })
      } else {
          setStatus({ locked: true })
          props.showNotification({
              type: 'failure',
              title: props.t('acctLockedTitle'),
              message: props.t('acctLocked')
          })
          if (values.passwordResetLockedAccountSent !== true) {
              accountLocked(values.userName, values.appName).then(response => {
                  values.passwordResetLockedAccountSent = true
              })
          }
          
        setStatus({locked: true})
        props.showNotification({
          type: 'failure',
          title: props.t('acctLockedTitle'),
          message: props.t('acctLocked')
        })
      }
    });
  }
})(LoginComponent)

export default withTranslation()(Login);
